import { use, useEffect, useRef, useState } from 'react'
import { useLingui } from '@lingui/react/macro'
import { Skeleton } from '@/components/ui/skeleton'
import { LazyOverlaySuspense, OverlayPortal, lazyOverlay, useLazyOverlay } from '@/components/custom/lazy-overlay'
import {
  DIALOG_FRAME_CLASS,
  DIALOG_VIEWPORT_CLASS,
  DialogEnterContext,
  SKELETON_EXIT_HOLD,
  useRecordEnter,
} from '@/components/ui/dialog-surface'
import { cn } from '@/lib/utils'
import {
  ALERT_DIALOG_DESCRIPTION_CLASS,
  ALERT_DIALOG_FOOTER_CLASS,
  ALERT_DIALOG_HEADER_CLASS,
  ALERT_DIALOG_OVERLAY_CLASS,
  ALERT_DIALOG_POPUP_CLASS,
  ALERT_DIALOG_TITLE_CLASS,
} from '@/components/ui/alert-dialog-surface'

interface ConfirmOptions {
  title: string
  description: string
  cancelLabel?: string
  confirmLabel?: string
  variant?: 'default' | 'destructive'
}

export interface ConfirmSurfaceProps extends ConfirmOptions {
  open: boolean
  onResult: (result: boolean) => void
}

// The alert dialog surface's exit is 100ms (`duration-100`); a skeleton closed before the surface arrived plays it.
const EXIT_MS = 150

// A local dialog, not a slot route (AGENTS.md "Dialogs are child routes"), but not in the page's bundle either: its
// surface is fetched on the first `confirm()`, or earlier when an action menu opens (ui/responsive-menu.tsx).
const ConfirmDialogSurface = lazyOverlay(() => import('./confirm-dialog-surface').then((module) => module.default))
export const preloadConfirm = ConfirmDialogSurface.preload

export function useConfirm() {
  const [state, setState] = useState<(ConfirmOptions & { open: boolean }) | null>(null)
  const resolveRef = useRef<((value: boolean) => void) | null>(null)
  const open = state?.open ?? false
  const { mounted } = useLazyOverlay(open)

  const confirm = (options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve
      setState({ ...options, open: true })
    })
  }

  const handleResult = (result: boolean) => {
    resolveRef.current?.(result)
    resolveRef.current = null
    setState((prev) => (prev ? { ...prev, open: false } : null))
  }

  const surface: ConfirmSurfaceProps = {
    open,
    title: state?.title ?? '',
    description: state?.description ?? '',
    cancelLabel: state?.cancelLabel,
    confirmLabel: state?.confirmLabel,
    variant: state?.variant,
    onResult: handleResult,
  }
  const dialog = mounted && (
    <LazyOverlaySuspense
      open={open}
      exitMs={EXIT_MS}
      skeleton={(isOpen) => <ConfirmSkeleton {...surface} open={isOpen} />}
    >
      <ConfirmDialogSurface {...surface} />
    </LazyOverlaySuspense>
  )

  return [confirm, dialog] as const
}

/**
 * The confirm's own box on the frame it opens, while its surface is fetched: the title and description are known,
 * so they paint as text; the buttons are not live yet, so they paint as skeletons of their labels.
 */
function ConfirmSkeleton(props: ConfirmSurfaceProps) {
  const { t } = useLingui()
  const { open, onResult } = props
  const popupRef = useRef<HTMLDivElement>(null)
  useRecordEnter(popupRef, use(DialogEnterContext), open)
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onResult(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onResult])

  const state = open ? { 'data-open': '' } : { 'data-closed': '' }
  const button =
    'inline-flex h-8 items-center justify-center rounded-lg px-2.5 text-sm font-medium whitespace-nowrap text-transparent select-none'
  return (
    <OverlayPortal>
      {/* The surface's own enter and exit: the real one carries the enter on (ui/dialog-surface.ts). */}
      <div
        {...state}
        data-slot="confirm-skeleton-overlay"
        className={cn(ALERT_DIALOG_OVERLAY_CLASS, SKELETON_EXIT_HOLD)}
        onClick={() => onResult(false)}
      />
      <div {...state} data-slot="confirm-skeleton-viewport" className={DIALOG_VIEWPORT_CLASS}>
        <div
          className={DIALOG_FRAME_CLASS}
          onClick={(event) => {
            if (event.target === event.currentTarget) onResult(false)
          }}
        >
          <div
            ref={popupRef}
            {...state}
            data-size="default"
            data-slot="confirm-skeleton"
            role="alertdialog"
            aria-modal="true"
            aria-busy="true"
            aria-label={props.title}
            className={cn(ALERT_DIALOG_POPUP_CLASS, SKELETON_EXIT_HOLD)}
          >
            <div className={ALERT_DIALOG_HEADER_CLASS}>
              <h2 className={ALERT_DIALOG_TITLE_CLASS}>{props.title}</h2>
              <p className={ALERT_DIALOG_DESCRIPTION_CLASS}>{props.description}</p>
            </div>
            <div className={ALERT_DIALOG_FOOTER_CLASS}>
              <Skeleton className={button}>{props.cancelLabel ?? t`Cancel`}</Skeleton>
              <Skeleton className={button}>{props.confirmLabel ?? t`Confirm`}</Skeleton>
            </div>
          </div>
        </div>
      </div>
    </OverlayPortal>
  )
}
